import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useToast } from './ToastProvider'
import { subscribeToServiceWorkerUpdates, type ActivateUpdate } from '../../serviceWorkerUpdate'

/**
 * Consent gate for service-worker updates: shows a sticky toast when a new
 * version is waiting and activates it only if the user accepts. Replaces the
 * previous auto-SKIP_WAITING takeover, which reloaded the tab whenever the
 * hourly update check found a deploy, killing long-running work mid-flight
 * (on-device model downloads, chat turns, exports).
 *
 * Mounted once inside <ToastProvider> (main.tsx); renders nothing until an
 * update is announced.
 */
export function AppUpdateToast() {
  const toast = useToast()
  const { t } = useTranslation()
  const shownFor = useRef<ActivateUpdate | null>(null)
  const toastId = useRef<string | null>(null)

  useEffect(
    () =>
      subscribeToServiceWorkerUpdates((activate) => {
        // A newer announcement replaces the pending toast instead of stacking.
        if (shownFor.current === activate) return
        shownFor.current = activate
        if (toastId.current) toast.dismiss(toastId.current)
        toastId.current = toast.toast({
          variant: 'info',
          title: t('pwa.updateAvailable.title', 'A new version is available'),
          description: t(
            'pwa.updateAvailable.description',
            'Reload to pick it up. You can also reload later.',
          ),
          // Sticky on purpose: consent must not time out.
          duration: 0,
          action: { label: t('pwa.updateAvailable.reload', 'Reload'), onClick: activate },
        })
      }),
    [toast, t],
  )

  return null
}
